import { useState } from 'react';
import { message } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { BatchDownloadService, DEFAULT_BATCH_CONFIG } from '../services/batchDownloadService';
import { DataMapperService } from '../services/dataMapperService';
import { saveTable2ExcelFile } from '../utils/saveTable2Excel';
import { DOWNLOAD_ALL_ORDERS_FOR_CENTRAL } from '../pages/central/parcels/graphql/queries/downloadAllOrdersForCentral';
import {formatDateTimeRangeToISO} from "../utils/dateTimeUtils";

// Hook返回的接口
export interface UseParcelDownloadResult {
    downloadLoading: boolean;
    downloadParcels: (filters: any) => Promise<void>;
}

// 自定义Hook用于包裹数据的分批下载
export function useParcelDownload(): UseParcelDownloadResult {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadAllOrders] = useLazyQuery(DOWNLOAD_ALL_ORDERS_FOR_CENTRAL);
    
    // 初始化下载服务
    const downloadService = new BatchDownloadService(DEFAULT_BATCH_CONFIG);
    
    // 获取单批次数据的函数
    const fetchBatch = async (page: number, size: number, queryFilters: any) => {
        // 直接使用已经转换过的 queryFilters，不需要重复转换时间范围
        const { data } = await downloadAllOrders({
            variables: { 
                filters: queryFilters,
                pageRequest: {
                    page: page,
                    size: Number(size),
                    sortBy: "orderTime",
                    sortDirection: "DESC"
                }
            }
        });
        return data?.getOrderListingForCentralOptimized?.content || [];
    };
    
    // 获取数据总数
    const getTotalCount = async (queryFilters: any): Promise<number> => {
        const { data } = await downloadAllOrders({
            variables: { 
                filters: queryFilters,
                pageRequest: {
                    page: 1,
                    size: 1, // 只获取1条数据来获取总数
                    sortBy: "orderTime",
                    sortDirection: "DESC"
                }
            }
        });
        return data?.getOrderListingForCentralOptimized?.totalElements || 0;
    };
    
    // 直接下载小数据量
    const downloadDirectly = async (totalCount: number, queryFilters: any) => {
        const hideLoading = message.loading('正在下载数据...', 0);
        try {
            const { data } = await downloadAllOrders({
                variables: { 
                    filters: queryFilters,
                    pageRequest: {
                        page: 1,
                        size: Number(totalCount),
                        sortBy: "orderTime",
                        sortDirection: "DESC"
                    }
                }
            });
            
            if (data?.getOrderListingForCentralOptimized?.content) {
                const orders = data.getOrderListingForCentralOptimized.content;
                hideLoading?.();
                return orders;
            } else {
                hideLoading?.();
                throw new Error('没有找到符合条件的数据');
            }
        } catch (error) {
            hideLoading?.();
            throw error;
        }
    };
    
    // 导出数据为Excel
    const exportToExcel = (orders: any[]) => {
        const exportData = DataMapperService.mapOrdersToExcelData(orders);
        if (exportData.length === 0){
            message.warning('没有找到符合条件数据');
            return;
        }
        const columns = DataMapperService.getExcelColumns();
        
        saveTable2ExcelFile(exportData, columns, 'Central_Parcels');
        message.success(`成功导出 ${exportData.length} 条数据`);
    };
    
    // 主下载函数
    const downloadParcels = async (filters: any) => {
        let hideLoading: (() => void) | undefined;
        
        try {
            setDownloadLoading(true);
            
            // 构建查询参数
            const timeRanges = {
                orderTime: formatDateTimeRangeToISO(filters.orderTime),
                destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
                destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
                deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
                receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
                markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
                updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
            };

            // 处理 receiverZipcode：如果有原始输入，转换为数组（与查询列表保持一致）
            let receiverZipcode: string[] | undefined = filters.receiverZipcode;
            if (filters.receiverZipcodeInput) {
                // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
                const zipcodes = filters.receiverZipcodeInput
                    .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                    .map((z: string) => z.trim())
                    .filter((z: string) => z.length > 0);
                receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
            }

            // 与查询列表保持一致的过滤项构造，避免把字符串形式的数组传给后端
            const queryFilters: any = {
                ...filters,
                ...timeRanges,
                // 转换数组类型为多值查询
                channelIds: filters.channelId?.map((id: string) => parseInt(id)) || undefined,
                pickupHubIds: filters.pickupHubId?.map((id: string) => parseInt(id)) || undefined,
                overallStatuses: filters.overallStatus || undefined,
                nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
                deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
                receiverZipcode: receiverZipcode, // 使用转换后的数组
                pickupTaskDate: undefined, // 不发送到后端
                // 转换 hubId 和 warehouseId 为数字类型（与 useCentralParcelQuery 保持一致）
                hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
                warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined,
                // 明确保留分拣码段字段，确保筛选条件正确传递
                sortingCodeSegment1: filters.sortingCodeSegment1 || undefined,
                sortingCodeSegment2: filters.sortingCodeSegment2 || undefined,
                sortingCodeSegment3: filters.sortingCodeSegment3 || undefined
            };
            // 清空临时输入字段，不发送到后端
            queryFilters.receiverZipcodeInput = undefined;

            // 清空单值字段，避免与多值字段冲突，并且防止把诸如 "[CREATED]" 的字符串传给 GraphQL
            queryFilters.channelId = undefined;
            queryFilters.pickupHubId = undefined;
            queryFilters.overallStatus = undefined;
            queryFilters.nextAction = undefined;
            queryFilters.deliveredDspId = undefined;

            // 移除无效的时间范围字段，避免GraphQL schema错误
            Object.keys(queryFilters).forEach(key => {
                const value = queryFilters[key];
                if (value === undefined) {
                    delete queryFilters[key];
                    return;
                }
                if (typeof value === 'object' && value !== null) {
                    const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
                    const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
                    if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                        delete queryFilters[key];
                    }
                }
            });

            // 先获取总数
            hideLoading = message.loading('正在获取数据总数...', 0);
            const totalCount = await getTotalCount(queryFilters);
            hideLoading?.();

            if (totalCount === 0) {
                message.error('没有找到符合条件的数据');
                return;
            }

            let orders: any[];

            // 判断是否需要分批下载
            if (downloadService.shouldUseBatchDownload(totalCount)) {
                // 大数据量，分批下载并合并
                message.info(`数据量较大 (${totalCount} 条)，将分 ${downloadService.getTotalBatches(totalCount)} 批次下载...`);
                
                // 分批从后端获取数据，在前端合并成一个大数组
                orders = await downloadService.downloadInBatches(
                    totalCount, 
                    (page, size) => fetchBatch(page, size, queryFilters)
                );
                
                if (orders.length > 0) {
                    // 将所有批次合并的数据生成一个Excel文件
                    message.success(`数据合并完成！正在生成包含 ${orders.length} 条数据的Excel文件...`);
                } else {
                    message.error('没有找到符合条件的数据');
                    return;
                }
            } else {
                // 小数据量，直接下载
                orders = await downloadDirectly(totalCount, queryFilters);
            }
            
            // 导出Excel
            exportToExcel(orders);
            
        } catch (error) {
            console.error('下载失败:', error);
            hideLoading?.();
            message.error('下载失败，请稍后重试');
        } finally {
            setDownloadLoading(false);
        }
    };
    
    return {
        downloadLoading,
        downloadParcels
    };
} 